import { getPowerLogs } from '@/services/xat-proxy.service';
import { fetchFairtradeCatalog } from '@/services/fairtrade.service';

export type FairtradePriceRange = {
  min: number;
  max: number;
};

export type FairtradeCurrency = 'xats' | 'days';

export type FairtradeCurrentQuote = {
  minXats: number;
  maxXats: number;
  minDays: number;
  maxDays: number;
};

export type FairtradePriceLogEntry = {
  date: string;
  dateInt: number;
  oldRange: FairtradePriceRange | null;
  newRange: FairtradePriceRange | null;
  oldDaysRange: FairtradePriceRange | null;
  newDaysRange: FairtradePriceRange | null;
  oldLabel: string;
  newLabel: string;
};

export type FairtradeChartPoint = {
  dateInt: number;
  label: string;
  fullDate: string;
  xatsMin: number;
  xatsMax: number;
  xatsMid: number;
  daysMin: number | null;
  daysMax: number | null;
  daysMid: number | null;
};

export type FairtradeHistoryResponse = {
  code: number;
  logs: FairtradePriceLogEntry[];
  chart: FairtradeChartPoint[];
  quote: FairtradeCurrentQuote | null;
  /** Days do histórico convertidos via taxa xats/days actual (API não regista days). */
  daysEstimated: boolean;
  source: string;
};

export type FairtradeHistoryQuoteHint = Partial<FairtradeCurrentQuote>;

const POWERLOGS_SOURCE = 'https://api.xatblog.net/powerlogs';

const RANGE_RE = /Min:\s*(\d+)\s*Max:\s*(\d+)/i;

export function parseFairtradeRangeLabel(text: string): FairtradePriceRange | null {
  const match = RANGE_RE.exec(text.trim());
  if (!match) return null;
  const min = Number.parseInt(match[1], 10);
  const max = Number.parseInt(match[2], 10);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { min, max };
}

export function formatFairtradeRangeLabel(
  range: FairtradePriceRange | null,
  currency: FairtradeCurrency = 'xats'
): string {
  if (!range) return '—';
  const unit = currency === 'days' ? 'days' : 'xats';
  if (range.min === range.max) {
    return `${range.min.toLocaleString('pt-BR')} ${unit}`;
  }
  return `${range.min.toLocaleString('pt-BR')} – ${range.max.toLocaleString('pt-BR')} ${unit}`;
}

export function formatFairtradeLogDate(raw: string): string {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})/.exec(raw.trim());
  if (!match) return raw;
  const [, day, month, year, hour, minute] = match;
  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute)
  );
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatChartTick(raw: string): string {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(raw.trim());
  if (!match) return raw.slice(0, 8);
  const [, day, month, year] = match;
  return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year.slice(2)}`;
}

/** Taxa média xats por day a partir da cotação actual. */
export function resolveXatsPerDayRate(quote: FairtradeCurrentQuote): number | null {
  const rates: number[] = [];
  if (quote.minDays > 0 && quote.minXats > 0) {
    rates.push(quote.minXats / quote.minDays);
  }
  if (quote.maxDays > 0 && quote.maxXats > 0) {
    rates.push(quote.maxXats / quote.maxDays);
  }
  if (rates.length === 0) return null;
  return rates.reduce((sum, n) => sum + n, 0) / rates.length;
}

export function convertXatsRangeToDays(
  range: FairtradePriceRange | null,
  xatsPerDay: number | null
): FairtradePriceRange | null {
  if (!range || !xatsPerDay || xatsPerDay <= 0) return null;
  const min = Math.max(1, Math.round(range.min / xatsPerDay));
  const max = Math.max(min, Math.round(range.max / xatsPerDay));
  return { min, max };
}

function midOf(range: FairtradePriceRange): number {
  return (range.min + range.max) / 2;
}

export function getFairtradeChangeDirection(
  oldRange: FairtradePriceRange | null,
  newRange: FairtradePriceRange | null
): 'up' | 'down' | 'same' | 'unknown' {
  if (!oldRange || !newRange) return 'unknown';
  const oldMid = midOf(oldRange);
  const newMid = midOf(newRange);
  if (newMid > oldMid) return 'up';
  if (newMid < oldMid) return 'down';
  return 'same';
}

function withDays(
  entry: Omit<FairtradePriceLogEntry, 'oldDaysRange' | 'newDaysRange'>,
  rate: number | null
): FairtradePriceLogEntry {
  return {
    ...entry,
    oldDaysRange: convertXatsRangeToDays(entry.oldRange, rate),
    newDaysRange: convertXatsRangeToDays(entry.newRange, rate),
  };
}

/** Série cronológica para gráficos (antigo → recente). */
export function buildFairtradeChartSeries(
  logs: FairtradePriceLogEntry[]
): FairtradeChartPoint[] {
  const chronological = [...logs].sort((a, b) => a.dateInt - b.dateInt);
  const points: FairtradeChartPoint[] = [];

  const pushPoint = (
    date: string,
    dateInt: number,
    xats: FairtradePriceRange | null,
    days: FairtradePriceRange | null
  ) => {
    if (!xats) return;
    points.push({
      dateInt,
      label: formatChartTick(date),
      fullDate: formatFairtradeLogDate(date),
      xatsMin: xats.min,
      xatsMax: xats.max,
      xatsMid: midOf(xats),
      daysMin: days?.min ?? null,
      daysMax: days?.max ?? null,
      daysMid: days ? midOf(days) : null,
    });
  };

  for (let i = 0; i < chronological.length; i += 1) {
    const entry = chronological[i];
    if (i === 0 && entry.oldRange) {
      pushPoint(entry.date, entry.dateInt - 1, entry.oldRange, entry.oldDaysRange);
    }
    pushPoint(entry.date, entry.dateInt, entry.newRange, entry.newDaysRange);
  }

  return points;
}

function normalizeLogs(
  monthlyLogs: unknown[],
  rate: number | null
): FairtradePriceLogEntry[] {
  return monthlyLogs
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const oldLabel = typeof row.old === 'string' ? row.old : '';
      const newLabel = typeof row.new === 'string' ? row.new : '';
      const date = typeof row.date === 'string' ? row.date : '';
      const dateInt = typeof row.date_int === 'number' ? row.date_int : 0;

      return withDays(
        {
          date,
          dateInt,
          oldRange: parseFairtradeRangeLabel(oldLabel),
          newRange: parseFairtradeRangeLabel(newLabel),
          oldLabel,
          newLabel,
        },
        rate
      );
    })
    .filter((entry): entry is FairtradePriceLogEntry => entry !== null)
    .sort((a, b) => b.dateInt - a.dateInt);
}

export function normalizePowerlogsResponse(
  raw: Record<string, unknown>,
  quote: FairtradeCurrentQuote | null = null
): FairtradeHistoryResponse {
  const monthlyLogs = Array.isArray(raw.monthly_logs) ? raw.monthly_logs : [];
  const rate = quote ? resolveXatsPerDayRate(quote) : null;
  const logs = normalizeLogs(monthlyLogs, rate);

  return {
    code: typeof raw.code === 'number' ? raw.code : 200,
    logs,
    chart: buildFairtradeChartSeries(logs),
    quote,
    daysEstimated: Boolean(rate && logs.some((l) => l.newDaysRange || l.oldDaysRange)),
    source: POWERLOGS_SOURCE,
  };
}

function hintToQuote(hint?: FairtradeHistoryQuoteHint): FairtradeCurrentQuote | null {
  if (!hint) return null;
  const minXats = Number(hint.minXats) || 0;
  const maxXats = Number(hint.maxXats) || 0;
  const minDays = Number(hint.minDays) || 0;
  const maxDays = Number(hint.maxDays) || 0;
  if (!minXats && !maxXats && !minDays && !maxDays) return null;
  return { minXats, maxXats, minDays, maxDays };
}

async function resolveQuote(
  slug: string,
  hint?: FairtradeHistoryQuoteHint
): Promise<FairtradeCurrentQuote | null> {
  const fromHint = hintToQuote(hint);
  if (
    fromHint &&
    (fromHint.minDays > 0 || fromHint.maxDays > 0) &&
    (fromHint.minXats > 0 || fromHint.maxXats > 0)
  ) {
    return fromHint;
  }

  try {
    const rows = await fetchFairtradeCatalog();
    const row = rows.find((r) => r.slug === slug || r.name === slug);
    if (!row) return fromHint;
    return {
      minXats: row.minXats,
      maxXats: row.maxXats,
      minDays: row.minDays,
      maxDays: row.maxDays,
    };
  } catch {
    return fromHint;
  }
}

export async function fetchPowerFairtradeHistory(
  slug: string,
  hint?: FairtradeHistoryQuoteHint
): Promise<FairtradeHistoryResponse> {
  const key = slug.trim().toLowerCase();
  if (!key) {
    return {
      code: 400,
      logs: [],
      chart: [],
      quote: null,
      daysEstimated: false,
      source: POWERLOGS_SOURCE,
    };
  }

  const [data, quote] = await Promise.all([getPowerLogs(key), resolveQuote(key, hint)]);
  return normalizePowerlogsResponse(data, quote);
}
